import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useT } from '../context/LanguageContext';
import LanguageToggle from '../components/i18n/LanguageToggle';
import GoogleSignInButton from '../components/GoogleSignInButton';
import { Eye, EyeOff, ArrowRight } from 'lucide-react';

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const t = useT();
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(form.email, form.password);
      navigate('/dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const set = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  return (
    <div className="min-h-screen bg-[#FAF7F0] flex items-center justify-center p-4 relative">
      <div className="absolute top-4 right-4 z-10"><LanguageToggle variant="light" /></div>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <span className="text-2xl font-bold text-[#1A2F4A]" style={{ fontFamily: "'Playfair Display', serif" }}>SELORA</span>
          </Link>
          <p className="text-sm text-[#6B4E37] mt-2">{t({ fr: 'Bon retour sur votre parcours de français.', en: 'Welcome back to your French journey.' })}</p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg border border-[#E8E4DC] p-8">
          <h1 className="text-2xl font-bold text-[#1A2F4A] text-center mb-6" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="login-title">
            {t({ fr: 'Se connecter', en: 'Log In' })}
          </h1>

          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3 mb-5" data-testid="login-error">{error}</div>}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#1A2F4A] mb-1.5">{t({ fr: 'Adresse e-mail', en: 'Email Address' })}</label>
              <input type="email" value={form.email} onChange={set('email')} required placeholder="you@example.com"
                className="w-full px-4 py-3 border border-[#E8E4DC] rounded-xl focus:outline-none focus:border-[#D4AF37] text-[#1A2F4A] bg-[#FAFAF8]"
                data-testid="login-email" />
            </div>
            <div className="relative">
              <label className="block text-sm font-medium text-[#1A2F4A] mb-1.5">{t({ fr: 'Mot de passe', en: 'Password' })}</label>
              <input type={showPassword ? 'text' : 'password'} value={form.password} onChange={set('password')} required placeholder={t({ fr: 'Entrez votre mot de passe', en: 'Enter your password' })}
                className="w-full px-4 py-3 border border-[#E8E4DC] rounded-xl focus:outline-none focus:border-[#D4AF37] text-[#1A2F4A] bg-[#FAFAF8] pr-12"
                data-testid="login-password" />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-9 text-[#999] hover:text-[#1A2F4A]">
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>

            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center gap-2 text-[#6B4E37] cursor-pointer">
                <input type="checkbox" className="rounded border-[#E8E4DC] text-[#D4AF37] focus:ring-[#D4AF37]" />
                {t({ fr: 'Rester connecté', en: 'Keep me logged in' })}
              </label>
              <Link to="/forgot-password" className="text-[#1A2F4A] font-medium hover:text-[#D4AF37] transition-colors" data-testid="forgot-password-link">
                {t({ fr: 'Mot de passe oublié ?', en: 'Forgot Password?' })}
              </Link>
            </div>

            <button type="submit" disabled={loading}
              className="w-full bg-[#1A2F4A] hover:bg-[#243a58] text-white py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="login-submit">
              {loading ? t({ fr: 'Connexion...', en: 'Logging in...' }) : t({ fr: 'Se connecter', en: 'Login' })}
              {!loading && <ArrowRight className="w-4 h-4" />}
            </button>
          </form>

          {/* Divider */}
          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-[#E8E4DC]" />
            <span className="text-[10px] tracking-[0.25em] font-bold text-[#6B4E37] uppercase">
              {t({ fr: 'ou', en: 'or' })}
            </span>
            <div className="flex-1 h-px bg-[#E8E4DC]" />
          </div>

          <GoogleSignInButton />

          <p className="text-center text-sm text-[#6B4E37] mt-6">
            {t({ fr: "Pas encore de compte ?", en: "Don't have an account?" })} <Link to="/signup" className="text-[#1A2F4A] font-semibold hover:text-[#D4AF37] transition-colors">{t({ fr: 'Créer un compte gratuit', en: 'Create one free' })}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
